import { createElement, useEffect, useLayoutEffect, useRef, type JSX } from "react";
import type { TcDialog } from "@tc-superpods/components/tc/types";
import type { DialogController } from "../dialog-controller";
import type { DialogView } from "../types";

export interface DialogLayerProps {
  dialogs: DialogView[];
  controller: DialogController;
}

export function DialogLayer({ dialogs, controller }: DialogLayerProps): JSX.Element | null {
  if (dialogs.length === 0) {
    return null;
  }

  return (
    <section data-platform-dialog-layer="" aria-label="Dialog layer">
      {dialogs.map((dialog, index) => (
        <DialogFrame
          key={dialog.id}
          dialog={dialog}
          isActive={index === dialogs.length - 1}
          controller={controller}
        />
      ))}
    </section>
  );
}

interface DialogFrameProps {
  dialog: DialogView;
  isActive: boolean;
  controller: DialogController;
}

function DialogFrame({ dialog, isActive, controller }: DialogFrameProps): JSX.Element {
  const dialogRef = useRef<TcDialog | null>(null);

  useLayoutEffect(() => {
    const element = dialogRef.current;
    if (!element) {
      return;
    }

    element.open = true;
    element.lightDismiss = dialog.dismissible;
    element.label = dialog.title ?? "";
  }, [dialog.dismissible, dialog.title]);

  useEffect(() => {
    const element = dialogRef.current;
    if (!element) {
      return;
    }

    const handleHide = (event: Event): void => {
      // tc-hide is a bubbling, composed lifecycle event. Ignore hide requests from controls
      // mounted inside this dialog (for example, a select closing its popup).
      if (event.target !== element) {
        return;
      }

      if (!dialog.dismissible) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      controller.dismissDialog(dialog.id);
    };

    element.addEventListener("tc-hide", handleHide);

    return () => {
      element.removeEventListener("tc-hide", handleHide);
    };
  }, [controller, dialog.dismissible, dialog.id]);

  return createElement(
    "tc-dialog",
    {
      "data-platform-dialog-component": "",
      "data-dialog-active": isActive ? "true" : "false",
      "data-dialog-id": dialog.id,
      "data-dialog-owner-app": dialog.ownerAppId,
      "data-dialog-size": dialog.size,
      "data-dialog-dismissible": dialog.dismissible ? "true" : "false",
      ref: dialogRef
    },
    <div
      data-platform-dialog-body-host=""
      ref={(element) => {
        controller.setHostElement(dialog.id, element);
      }}
    />
  );
}
